import { ReactNode, useEffect, useMemo, useState } from 'react';
import { BsExclamation, BsCheckLg } from 'react-icons/bs';
import { GoPlus } from 'react-icons/go';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import { useTournament } from '@/hooks/useTournament';
import { TournamentStatus } from '@/types';
import { cn, addOpacityToHexColor } from '@/utils';
import { COLORS } from '@/utils/constants';

type TagsProps = {
  variant?: number | 'asegurado' | 'capitan' | 'delete' | undefined;
  textClassName?: string;
  className?: string;
  textTooltip?: string;
  tournamentStatus?: TournamentStatus[];
  forceIcon?: ReactNode;
};

type IconsProps = {
  textTooltip?: string;
  icon: ReactNode;
  bgColor: string;
  borderColor?: string;
  toolTipBgColor?: string;
  textTooltipColor: string;
  shadowColor?: string;
};

const initState = {
  textTooltip: '',
  icon: <></>,
  bgColor: '',
  borderColor: '',
  toolTipBgColor: '',
  textTooltipColor: '',
  shadowColor: '',
};

export const IconPlayerStatus = ({
  variant,
  className,
  forceIcon,
}: TagsProps) => {
  const { tournamentMap } = useTournament();
  const [state, setState] = useState<IconsProps>(initState);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const id = useMemo(() => Math.floor(Math.random() * 1000), [variant]);

  const tournamentStatusMap = tournamentMap?.tournamentStatusMap;

  const tagClass = cn(
    'absolute flex text-xs items-center not-italic justify-center rounded-[50%] font-bold text-white border-white w-[16px] h-[16px] lg:w-[20px] lg:h-[20px] border-[1.35px]',
    className
  );

  useEffect(() => {
    const defaultData = {
      icon: <GoPlus className="w-full" size={18} />,
      bgColor: COLORS['gdt-blue'].dark,
      shadowColor: addOpacityToHexColor('#455278', '50%'),
      textTooltip: '',
      textTooltipColor: '#FFF',
    };

    if (typeof variant === 'number') {
      const tournamentStatus = tournamentStatusMap
        ? tournamentStatusMap.get(variant)
        : undefined;
      const useCheckIcon = tournamentStatus?.id === 104; // ID para 'Posible Titular'

      if (tournamentStatus) {
        setState({
          textTooltip: tournamentStatus.nombre,
          icon: useCheckIcon ? (
            <BsCheckLg className="w-full" size={14} />
          ) : (
            <BsExclamation className="w-full" size={20} />
          ),
          toolTipBgColor: `#${tournamentStatus.color}`,
          bgColor: `#${tournamentStatus.color}`,
          shadowColor: addOpacityToHexColor(
            `#${tournamentStatus.color}`,
            '50%'
          ),
          textTooltipColor: '#FFF',
        });
      } else {
        setState(defaultData);
      }
    } else if (variant === 'asegurado') {
      setState({
        textTooltip: 'ASEGURADO',
        icon: <span className="leading-[0] text-gdt-blue block">A</span>,
        bgColor: '#FFF',
        borderColor: '#FFF',
        toolTipBgColor: '#FFF',
        textTooltipColor: '#3366FF',
      });
    } else if (variant === 'capitan') {
      setState({
        textTooltip: 'CAPITÁN',
        icon: <span className="leading-[0] block">C</span>,
        bgColor: '#3366FF',
        toolTipBgColor: '#3366FF',
        borderColor: '#3366FF',
        textTooltipColor: '#FFF',
      });
    } else {
      setState(defaultData);
    }
  }, [tournamentStatusMap, variant]);

  return (
    <div>
      <div
        className={cn(tagClass, {
          'cursor-pointer': state.textTooltip && state.textTooltip.length > 0,
        })}
        data-tooltip-id={`tooltip-${id}`}
        role="status"
        style={{
          backgroundColor: state?.bgColor,
          color: variant === 'asegurado' ? '#3366FF' : '#FFF',
          ...('shadowColor' in state && {
            boxShadow: `0px 0px 16px 0px ${state.shadowColor}`,
          }),
          ...('borderColor' in state && {
            borderColor: state.borderColor,
          }),
        }}
      >
        {forceIcon ?? state.icon}
      </div>
      {/* Solo muestro el tooltip si hay contenido que mostrar */}
      {/* Sino se redibujara un elemento en el DOM que no se esta viendo */}
      {state.textTooltip && state.textTooltip.length > 0 && (
        <ReactTooltip
          content={state?.textTooltip.toUpperCase()}
          id={`tooltip-${id}`}
          place="top"
          style={{
            backgroundColor:
              'toolTipBgColor' in state ? state?.toolTipBgColor : state.bgColor,
            fontWeight: 700,
            padding: '2px 8px',
            lineHeight: '1',
            fontSize: '.6rem',
            color: state.textTooltipColor,
          }}
        />
      )}
    </div>
  );
};
